import React from "react";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import squidshot from "../media/games/squidshot.png";
import untitled_metroidvania from "../media/games/untitledmetroidvania.png";
import rocketjump from "../media/games/rocketjump.png";
import ttrpg from "../media/games/ttrpg.png";

const Games = () => {
  const getSortKey = (yearString) => {
    if (!yearString) return 0;
    // Extract the end year for sorting
    if (yearString.includes("-")) {
      const parts = yearString.split("-");
      if (parts[1] === "ongoing") {
        return 9999; // ongoing is most recent
      }
      return parseInt(parts[1]) || 0; // use end year
    }
    return parseInt(yearString) || 0; // single year
  };

  const games = [
    {
      id: 1,
      title: "Squid Shot",
      image: squidshot,
      status: "released",
      description:
        "Puzzle platformer where players traverse levels using only a grapple hook with limited uses per level. Navigate spikes and hazards to complete each challenge.",
      gameplay: "Puzzle Platformer",
      engine: "Unity",
      year: "2022",
      playLink: "https://kalekdan.itch.io/squidshot",
    },
    {
      id: 2,
      title: "Untitled Metroidvania",
      image: untitled_metroidvania,
      status: "in development",
      description:
        "Metroidvania currently in development where you play as a monster hunter freeing cursed creatures rather than killing them. Heavy focus on challenging platforming with Ori-like mechanics.",
      gameplay: "Metroidvania/Platformer",
      engine: "Unity",
      year: "2023-ongoing",
      devlogLink: "https://twitter.com/JoeRickardDev",
      youtubeLink: "https://www.youtube.com/channel/UCjxSTM3z4n_7ctEmCbusaLQ",
    },
    {
      id: 3,
      title: "Rocket Jump",
      image: rocketjump,
      status: "released",
      description:
        "One of the first games made - a simple 3D platformer using the classic rocket jumping mechanic. Single level with nostalgic charm showing early game development.",
      gameplay: "3D Platformer",
      engine: "Unreal Engine",
      year: "2021",
      playLink: "https://kalekdan.itch.io/squidshot",
    },
    {
      id: 4,
      title: "TTRPGs & Worldbuilding",
      image: ttrpg,
      status: "ongoing",
      description:
        "Homebrewed D&D campaign world with detailed wiki documentation. All campaigns documented with extensive lore, maps, and character information.",
      gameplay: "Tabletop RPG",
      engine: "D&D 5e",
      year: "2020-ongoing",
      wikiLink: "/wiki/doku.php?id=campaigns:the_isles_of_fate",
      wikiAllLink: "/wiki/doku.php",
    },
  ].sort((a, b) => getSortKey(b.year) - getSortKey(a.year));

  return (
    <>
      <div className="title">
        <h2 className="heading">games & worlds.</h2>
        <p className="section-subtitle">Playable experiences and adventures</p>
      </div>
            <div className="games-intro">
        <p>
          I like playing games (who doesn't) and I like making games. So I make games. Below are a selection of the few games which have made it past the prototpye stage (and some of these only barely).
        </p>
        <p>
          Over the 2020 lockdown, I also found a love for worldbuilding and playing tabletop RPGs, so I've included some of my homebrewed worlds here too.
        </p>
        <Button
          href="https://soundcloud.com/kalekdan"
          target="_blank"
          rel="noreferrer"
          variant="contained"
          sx={{
            backgroundColor: "var(--primary-color2)",
            color: "white",
            mt: 2,
            "&:hover": {
              backgroundColor: "var(--primary-color3)",
            },
          }}
        >
          Listen on SoundCloud
        </Button>
      </div>
      <div className="games-content">
        {games.map((game) => (
          <div key={game.id} className="games-element">
            <div className="game-header">
              <h2>{game.title}</h2>
              <Chip
                label={game.status}
                size="small"
                sx={{
                  bgcolor:
                    game.status === "released"
                      ? "green"
                      : game.status === "in development"
                      ? "orange"
                      : "#3498db",
                  color: "white",
                }}
              />
            </div>

            <img
              alt={game.title}
              src={game.image}
              loading="lazy"
              className="game-image"
            />

            <p className="game-description">{game.description}</p>

            <div className="game-info">
              <div className="info-item">
                <strong>Genre:</strong> {game.gameplay}
              </div>
              <div className="info-item">
                <strong>Engine:</strong> {game.engine}
              </div>
              <div className="info-item">
                <strong>Year:</strong> {game.year}
              </div>
            </div>

            <div className="game-links">
              {game.playLink && (
                <Button
                  href={game.playLink}
                  target="_blank"
                  rel="noreferrer"
                  variant="contained"
                  size="small"
                  sx={{
                    backgroundColor: "var(--primary-color2)",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "var(--primary-color3)",
                    },
                  }}
                >
                  Play Now
                </Button>
              )}
              {game.devlogLink && (
                <Button
                  href={game.devlogLink}
                  target="_blank"
                  rel="noreferrer"
                  variant="outlined"
                  size="small"
                  sx={{
                    borderColor: "var(--primary-color2)",
                    color: "var(--primary-color2)",
                  }}
                >
                  Follow Dev
                </Button>
              )}
              {game.youtubeLink && (
                <Button
                  href={game.youtubeLink}
                  target="_blank"
                  rel="noreferrer"
                  variant="outlined"
                  size="small"
                  sx={{
                    borderColor: "var(--primary-color2)",
                    color: "var(--primary-color2)",
                  }}
                >
                  DevLogs
                </Button>
              )}
              {game.wikiLink && (
                <Button
                  href={game.wikiLink}
                  target="_blank"
                  rel="noreferrer"
                  variant="contained"
                  size="small"
                  sx={{
                    backgroundColor: "var(--primary-color2)",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "var(--primary-color3)",
                    },
                  }}
                >
                  View World
                </Button>
              )}
              {game.wikiAllLink && (
                <Button
                  href={game.wikiAllLink}
                  target="_blank"
                  rel="noreferrer"
                  variant="outlined"
                  size="small"
                  sx={{
                    borderColor: "var(--primary-color2)",
                    color: "var(--primary-color2)",
                  }}
                >
                  All Campaigns
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default Games;
